'use client';

import { useTranslations } from 'next-intl';
import { type TransitionEvent, useEffect, useState } from 'react';
import { LottieIllustration } from '@/lib/ui-kit/lottie-illustration';
import { cn } from '@/lib/ui-kit/utils';
import { preloadDailyPick } from './dailyPickLoader';
import { useDailyCardOffer } from './useDailyCardOffer';

// Fade in place first, then close the slot: the icons hug the right edge and
// would carry a visible card along. Delays follow opacity, width, margin.
const GOING = 'pointer-events-none -mr-4 w-0 opacity-0 [transition-delay:0ms,300ms,300ms]';

/** The daily card in the header below 1024, before the bell (Figma 15027:231057, 231022). */
export function DailyCardHeaderButton() {
  const t = useTranslations('rewardsHub.daily');
  const { available, open } = useDailyCardOffer();
  // Stays mounted after `available` drops, until the fade has played.
  const [mounted, setMounted] = useState(available);

  useEffect(() => {
    if (available) setMounted(true);
  }, [available]);

  if (!mounted) return null;

  // The slot closing is the last step; the fade ends before it.
  const handleTransitionEnd = (event: TransitionEvent<HTMLButtonElement>) => {
    if (!available && event.propertyName === 'width') setMounted(false);
  };

  return (
    // The card's own 32×29 box (Figma 15027:1784), so the header's 16px gap is
    // the gap you see; the glow and sway spill past it.
    <button
      type="button"
      onClick={open}
      // A touch lands on pointerdown well before the click: start the chunks then.
      onPointerDown={preloadDailyPick}
      onPointerEnter={preloadDailyPick}
      onTransitionEnd={handleTransitionEnd}
      aria-label={t('widgetOpen')}
      // The header hides Deposit on a narrow phone while this is there.
      data-daily-card-header
      className={cn(
        'relative h-[29px] w-8 shrink-0 cursor-pointer transition-[opacity,width,margin] duration-300 ease-out min-[1024px]:hidden',
        !available && GOING,
      )}
    >
      <LottieIllustration
        name="daily-card-default"
        loop
        fill
        className="absolute top-1/2 left-1/2 h-[66px] w-[90px] -translate-x-1/2 -translate-y-1/2 overflow-visible"
      />
    </button>
  );
}
